import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Mail, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios, { API, setSessionToken } from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/layout/Footer";
import { trackLogin } from "@/utils/fbPixel";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { usePresenceTracking } from '@/hooks/usePresenceTracking';

const API_BASE = API.replace('/api', '');

const LoginPage = () => {
  const { login, user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  // Track presence on login page
  usePresenceTracking('signing_in');
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Preview mode state for B2B white-label preview
  const [previewClient, setPreviewClient] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  
  // Fetch client branding for preview mode
  useEffect(() => {
    const previewClientId = searchParams.get('preview');
    if (previewClientId) {
      setLoadingPreview(true);
      axios.get(`${API_BASE}/api/client-portal/public-branding/${previewClientId}`)
        .then(res => {
          setPreviewClient(res.data);
        })
        .catch(err => {
          console.error('Failed to load preview branding:', err);
          // Don't show error - just use default branding
        })
        .finally(() => {
          setLoadingPreview(false);
        });
    }
  }, [searchParams]);

  useEffect(() => {
    // Don't redirect in preview mode
    if (searchParams.get('preview')) {
      return;
    }
    
    if (!loading && user) {
      // Redirect based on onboarding status
      // Skip onboarding for B2B users if their client has disabled it
      const b2bOnboardingDisabled = user.b2b_client_id && user.client_features?.enable_onboarding === false;
      
      if (!user.onboarding_complete && !b2bOnboardingDisabled) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, loading, navigate, searchParams]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // In preview mode, show a message instead of actually logging in
    if (searchParams.get('preview')) {
      toast.info('This is preview mode. Login is disabled.', {
        description: 'Your clients will use this page to sign in to their accounts.'
      });
      return;
    }
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      const payload = {
        email: formData.email,
        password: formData.password
      };
      
      const response = await axios.post(`${API}/auth/login`, payload);
      
      // Store session token for API authentication (handles CORS with custom domains)
      if (response.data.session_token) {
        setSessionToken(response.data.session_token);
      }
      
      // Remove session_token from user data before storing
      const userData = { ...response.data };
      delete userData.session_token;
      
      setUser(userData);
      
      // Track Facebook Pixel login event
      trackLogin({ method: 'email' });
      
      toast.success('Welcome back!');
      
      // Redirect based on onboarding status
      // Skip onboarding for B2B users if their client has disabled it
      const b2bOnboardingDisabled = userData.b2b_client_id && userData.client_features?.enable_onboarding === false;
      
      if (!userData.onboarding_complete && !b2bOnboardingDisabled) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      
      // Handle cancelled subscription - redirect to resubscribe page
      if (message === 'SUBSCRIPTION_CANCELLED') {
        navigate('/resubscribe', { state: { email: formData.email } });
        return;
      }
      
      setErrors({ submit: message });
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // In preview mode, show the page even while auth is loading
  if (loading && !searchParams.get('preview')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2D063E] to-[#4A0A66]">
        <div className="loading-spinner" />
      </div>
    );
  }
  
  // Show loading state while fetching preview client data
  if (loadingPreview) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{
          background: 'linear-gradient(to bottom right, #2D063E, #4A0A66)'
        }}
      >
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col p-4 overflow-x-hidden"
      style={{
        background: 'linear-gradient(to bottom right, #2D063E, #4A0A66)'
      }}
    >
      <div className="flex-1 flex flex-col items-center justify-center pt-8 sm:pt-12">
        <div className="text-center max-w-lg w-full px-2">
          {/* Logo - Always show default UnCloned logo (login page is not white-labeled) */}
          <div className="w-full px-4 mb-6">
            <div 
              data-testid="login-logo"
              className="w-full h-28 sm:h-36 lg:h-40 animate-gradient-x mx-auto max-w-sm lg:max-w-md"
              style={{
                background: 'linear-gradient(90deg, #df1283 0%, #FF7901 25%, #df1283 50%, #FF7901 75%, #df1283 100%)',
                backgroundSize: '200% 100%',
                WebkitMaskImage: `url("/new-logo-homepage.png")`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskImage: `url("/new-logo-homepage.png")`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
              }}
            />
          </div>
          
          {/* Preview mode banner - only show if in preview mode */}
          {searchParams.get('preview') && (
            <div 
              className="mt-4 mb-2 px-3 py-2 rounded-lg text-xs font-medium bg-[#df1283]/20 border border-[#df1283]/50 text-[#df1283]"
            >
              🔍 Preview Mode — This is how the login page looks (not white-labeled)
            </div>
          )}
          
          <p data-testid="login-tagline" className="text-sm sm:text-base text-[#FDF2F8]/80 mb-6 px-2">
            Your private journal with mood tracking and AI coaching.
          </p>

          <div data-testid="login-form-card" className="glass-card-brand p-6 sm:p-8">
            {/* Sign In Header */}
            <h2 className="text-xl font-bold text-white text-center mb-5">SIGN IN</h2>

            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Email */}
              <div>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={`w-full px-4 py-2.5 rounded-xl bg-[#4A0A66]/30 border ${errors.email ? 'border-red-500' : 'border-[#df1283]/30'} text-white placeholder-[#FDF2F8]/50 focus:outline-none focus:border-[#df1283] text-sm`}
                  data-testid="email-input"
                />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Password */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className={`w-full px-4 py-2.5 pr-10 rounded-xl bg-[#4A0A66]/30 border ${errors.password ? 'border-red-500' : 'border-[#df1283]/30'} text-white placeholder-[#FDF2F8]/50 focus:outline-none focus:border-[#df1283] text-sm`}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FDF2F8]/50 hover:text-[#df1283]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-2 text-red-300 text-sm">
                  {errors.submit}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                onClick={(e) => {
                  // Ensure form submission works on click
                  if (!isSubmitting) {
                    handleSubmit(e);
                  }
                }}
                className="btn-brand-primary w-full py-2.5 text-sm font-semibold"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    SIGNING IN...
                  </span>
                ) : searchParams.get('preview') ? (
                  <span className="flex items-center justify-center gap-2">
                    <Eye className="w-4 h-4" />
                    SIGN IN (Preview)
                  </span>
                ) : (
                  'SIGN IN'
                )}
              </Button>

              {/* Forgot Password Link */}
              {!searchParams.get('preview') && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-[#df1283] hover:text-[#FF7901] text-xs transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              
              {/* Preview mode note */}
              {searchParams.get('preview') && (
                <div className="text-center">
                  <p className="text-[#FDF2F8]/50 text-xs">
                    Login is disabled in preview mode
                  </p>
                </div>
              )}
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-[#df1283]/30" />
              <span className="text-[#FDF2F8]/50 text-xs">or continue with</span>
              <div className="flex-1 h-px bg-[#df1283]/30" />
            </div>

            {/* Google Sign-In */}
            <Button
              onClick={() => {
                if (searchParams.get('preview')) {
                  toast.info('This is preview mode. Login is disabled.', {
                    description: 'Your clients will use this page to sign in with Google.'
                  });
                  return;
                }
                login();
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-white hover:bg-gray-100 text-gray-800 rounded-xl transition-colors text-sm font-medium"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </Button>
            
            {/* Create Account Link */}
            {!searchParams.get('preview') && (
              <div className="text-center mt-4 pt-4 border-t border-[#df1283]/20">
                <p className="text-[#FDF2F8]/60 text-sm">
                  Don't have an account?{' '}
                  <a 
                    href="/" 
                    className="text-[#df1283] hover:text-[#FF7901] font-semibold transition-colors"
                    data-testid="create-account-link"
                  >
                    Get Started
                  </a>
                </p>
              </div>
            )}
          </div>

          <p className="text-[#FDF2F8]/60 mt-5 text-xs px-2">
            Your journal is private and secure. Only you can see your entries.
          </p>
        </div>
      </div>
      
      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl p-6 w-full max-w-md">
            {forgotSent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Check Your Email</h2>
                <p className="text-[#FDF2F8]/70 mb-6">
                  If an account exists for {forgotEmail}, you&apos;ll receive a password reset link shortly.
                </p>
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotSent(false);
                    setForgotEmail('');
                  }}
                  className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-white">Reset Password</h2>
                  <button
                    onClick={() => setShowForgotPassword(false)}
                    className="text-[#FDF2F8]/50 hover:text-white"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-[#FDF2F8]/70 text-sm mb-4">
                  Enter your email address and we&apos;ll send you a link to reset your password.
                </p>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!forgotEmail) return;
                    
                    try {
                      setForgotSending(true);
                      await axios.post(`${API_BASE}/api/auth/forgot-password`, { email: forgotEmail });
                      setForgotSent(true);
                    } catch (err) {
                      // Always show success to prevent email enumeration
                      setForgotSent(true);
                    } finally {
                      setForgotSending(false);
                    }
                  }}
                  className="space-y-4"
                >
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDF2F8]/40" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#4A0A66]/30 border border-[#df1283]/30 text-white placeholder-[#FDF2F8]/50 focus:outline-none focus:border-[#df1283] text-sm"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      variant="outline"
                      className="flex-1 border-[#4A0A66] text-[#FDF2F8]/70"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={forgotSending || !forgotEmail}
                      className="flex-1 bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white"
                    >
                      {forgotSending ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
      
      <Footer useBrandColors={true} />
    </div>
  );
};

export default LoginPage;
